import { useCallback, useEffect, useState } from "react";
import {
  Alert, AlertTitle, Button, Chip, Divider, Link, MenuItem, Paper, Stack,
  TextField, Typography,
} from "@mui/material";
import { EmptyState, Loading } from "@lopie/kernel-ui";

import { api } from "../lib/oncall-api.ts";
import { useDirectory } from "../lib/people.ts";

const STATE = {
  triggered: { label: "Unanswered", colour: "kernel.refused" },
  acknowledged: { label: "Somebody has it", colour: "kernel.attention" },
  resolved: { label: "Resolved", colour: "kernel.permitted" },
};

/**
 * What is on fire.
 *
 * Unanswered first and always, whatever the clock says. An incident nobody has
 * acknowledged is the only thing on this screen that needs a person, and
 * sorting by time alone would bury it under an hour of resolved noise.
 */
export default function Incidents() {
  // Who acknowledged is stored as an id, which is correct, and was rendered
  // as one, which was not: "acknowledged by 01M1YRYH8RJWK43YTWB156Z4VP" on
  // the one card somebody reads during an outage (LO-107).
  const directory = useDirectory();
  const [incidents, setIncidents] = useState(null);
  const [failure, setFailure] = useState(null);
  const [busy, setBusy] = useState(null);
  const [services, setServices] = useState([]);

  const load = useCallback(() => {
    api.incidents()
      .then((body) => setIncidents(body.incidents ?? []))
      .catch((e) => setFailure(e.message));
  }, []);

  useEffect(() => {
    load();
    // Ten seconds. A list that needed a refresh to show a new page is a list
    // somebody stops trusting during the one hour it matters.
    const timer = setInterval(load, 10000);
    return () => clearInterval(timer);
  }, [load]);

  // What a declared incident could be routed to. Read once: services change
  // when somebody configures them, which is not during an outage.
  //
  // Failure is swallowed deliberately. Not being able to list services means
  // the declare control does not appear, and that is the right outcome —
  // there is nothing to declare *against*. Surfacing it as an error over the
  // incident list would put a red box on the screen somebody opened to find
  // out what is on fire.
  useEffect(() => {
    api.services()
      .then((body) => setServices(body.services ?? []))
      .catch(() => setServices([]));
  }, []);

  const act = async (id, what) => {
    setBusy(id);
    try {
      await (what === "ack" ? api.acknowledge(id) : api.resolve(id));
      load();
    } finally {
      setBusy(null);
    }
  };

  if (failure) {
    return (
      <Alert severity="error" variant="outlined">
        <AlertTitle>Could not read incidents</AlertTitle>
        {failure}
      </Alert>
    );
  }
  if (!incidents) return <Loading label="Reading incidents" />;

  if (incidents.length === 0) {
    return (
      <Stack spacing={2}>
        <EmptyState title="Nothing is on fire">
          <Typography variant="body2">
            No incident has been opened. A monitor opens one by posting to
            {" "}<code>/oncall/intake</code> with its service&apos;s routing
            key, and you can open one yourself below.
          </Typography>
        </EmptyState>
        <Declare services={services} onDone={load} />
      </Stack>
    );
  }

  const order = { triggered: 0, acknowledged: 1, resolved: 2 };
  const sorted = [...incidents].sort(
    (a, b) => (order[a.state] ?? 3) - (order[b.state] ?? 3)
      || String(b.openedAt).localeCompare(String(a.openedAt)),
  );

  return (
    <Stack spacing={2}>
      <Declare services={services} onDone={load} />
      {sorted.map((incident) => {
        const state = STATE[incident.state] ?? STATE.triggered;
        return (
          <Paper key={incident.id} variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack
              direction="row"
              spacing={1}
              sx={{ alignItems: "baseline", flexWrap: "wrap" }}
            >
              <Typography variant="h2" sx={{ flexGrow: 1 }}>
                {incident.title}
              </Typography>
              <Chip size="small" variant="outlined" label={incident.urgency} />
              <Chip
                size="small"
                variant="outlined"
                label={state.label}
                sx={{ color: state.colour, borderColor: state.colour }}
              />
            </Stack>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              Level {incident.currentLevel} · opened{" "}
              {new Date(incident.openedAt).toLocaleString()}
              {incident.acknowledgedBy
                ? ` · acknowledged by ${directory.nameOf(incident.acknowledgedBy)}`
                : ""}
            </Typography>

            {incident.state === "triggered" && incident.nextEscalationAt ? (
              <Typography variant="body2" sx={{ mt: 0.5, color: "kernel.attention" }}>
                Escalates at{" "}
                {new Date(incident.nextEscalationAt).toLocaleTimeString()} if
                nobody answers.
              </Typography>
            ) : null}

            {incident.state === "triggered" && !incident.nextEscalationAt ? (
              // The worst state this system produces, and it used to be
              // indistinguishable from an incident somebody had handled.
              <Alert severity="error" variant="outlined" sx={{ mt: 1.5 }}>
                <AlertTitle>Nobody answered, and nothing else will happen</AlertTitle>
                Every escalation level has been tried. This sits here until a
                person picks it up.
              </Alert>
            ) : null}

            {incident.externalUrl ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                <Link href={incident.externalUrl} target="_blank" rel="noreferrer">
                  {incident.externalUrl}
                </Link>
              </Typography>
            ) : null}

            <Divider sx={{ my: 1.5 }} />
            <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
              {incident.state === "triggered" ? (
                <Button
                  size="small"
                  variant="contained"
                  disabled={busy === incident.id}
                  onClick={() => act(incident.id, "ack")}
                >
                  Acknowledge
                </Button>
              ) : null}
              {incident.state !== "resolved" ? (
                <Button
                  size="small"
                  disabled={busy === incident.id}
                  onClick={() => act(incident.id, "resolve")}
                >
                  Resolve
                </Button>
              ) : null}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
}


/**
 * Declare an incident, and page whoever is on call for it.
 *
 * Most of what a pager is asked to do out of hours starts with somebody
 * noticing rather than with an alarm, so this is a feature and not a test
 * hook. It is also the only door that works today: `/oncall/intake` pages
 * through the app's own kernel token and a provisioned instance does not have
 * one, where this call carries the token of the person pressing the button.
 *
 * Collapsed until asked for. The screen's job is to say what is on fire, and
 * a form sitting open above that list competes with it every time somebody
 * opens the app during an outage.
 *
 * It says what will happen before it happens. "Page whoever is on call" is
 * the honest label for a button that rings somebody's phone at three in the
 * morning; "Submit" is not.
 */
function Declare({ services, onDone }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [serviceId, setServiceId] = useState("");
  const [urgency, setUrgency] = useState("high");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);

  // No services means nothing to route a page through — an escalation policy
  // is attached to a service, not to an incident — so the control would be a
  // form that can only fail. Said on the setup screen, not here.
  if (!services.length) return null;

  if (!open) {
    return (
      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button variant="outlined" onClick={() => setOpen(true)}>
          Declare an incident
        </Button>
      </Stack>
    );
  }

  const submit = async () => {
    setBusy(true);
    setProblem(null);
    try {
      await api.declare({ serviceId, title: title.trim(), urgency });
      setOpen(false);
      setTitle("");
      onDone();
    } catch (e) {
      setProblem(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, sm: 3 } }}>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
        Declare an incident
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        This pages whoever is on call for the service you pick, straight away.
      </Typography>

      <Stack spacing={2} sx={{ mt: 2 }}>
        <TextField
          size="small"
          label="What is wrong"
          placeholder="Checkout is returning 500s"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          disabled={busy}
        />
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
          <TextField
            select
            size="small"
            label="Service"
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
            disabled={busy}
            sx={{ minWidth: { sm: 220 } }}
          >
            {services.map((s) => (
              <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Urgency"
            value={urgency}
            onChange={(e) => setUrgency(e.target.value)}
            disabled={busy}
            sx={{ minWidth: { sm: 160 } }}
          >
            <MenuItem value="high">High — page now</MenuItem>
            <MenuItem value="low">Low</MenuItem>
          </TextField>
        </Stack>

        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={busy || !title.trim() || !serviceId}
            onClick={submit}
          >
            Page whoever is on call
          </Button>
          <Button variant="text" disabled={busy} onClick={() => setOpen(false)}>
            Cancel
          </Button>
        </Stack>

        {problem ? (
          <Alert severity="error" variant="outlined">
            <AlertTitle>That did not open an incident</AlertTitle>
            {problem}
          </Alert>
        ) : null}
      </Stack>
    </Paper>
  );
}
